import PropTypes from "prop-types";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Iconify from "../iconify";
import { getPositionLetter } from "src/utils/positionUtils";
import { getPositionsForSport } from "src/utils/scoreablesDashboardUtils";

const SPORT_LABELS = {
  hockey: "Hockey",
  soccer: "Soccer",
  football: "American Football",
  basketball: "Basketball",
  cricket: "Cricket",
  australian_football: "Australian Football",
  rugby: "Rugby",
};

PositionGlossary.propTypes = {
  sport: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedPositions: PropTypes.arrayOf(PropTypes.number),
  onTogglePosition: PropTypes.func,
  onClearPositions: PropTypes.func,
};

PositionGlossary.defaultProps = {
  selectedPositions: [],
};

export default function PositionGlossary({
  sport,
  selectedPositions,
  onTogglePosition,
  onClearPositions,
}) {
  const positions = getPositionsForSport(sport);
  const sportKey = typeof sport === "string" ? sport.toLowerCase() : null;
  const sportLabel = (sportKey && SPORT_LABELS[sportKey]) || SPORT_LABELS.hockey;
  const selectedNames = positions
    .filter((p) => selectedPositions.includes(p.value))
    .map((p) => p.name);

  return (
    <Accordion
      disableGutters
      sx={{
        mb: 2,
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
        boxShadow: "none",
        "&:before": { display: "none" },
        "&.Mui-expanded": {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        },
      }}
    >
      <AccordionSummary
        expandIcon={
          <Iconify
            icon="eva:arrow-ios-downward-outline"
            sx={{ color: "primary.main" }}
          />
        }
        sx={{
          px: 2.5,
          py: 0.5,
          minHeight: 64,
          "& .MuiAccordionSummary-content": {
            my: 1.25,
            alignItems: "center",
          },
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: "100%", pr: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
              color: "primary.main",
            }}
          >
            <Iconify icon="eva:book-open-outline" width={22} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Typography variant="subtitle1" fontWeight={700}>
                Position Glossary
              </Typography>
              <Chip
                size="small"
                label={`${positions.length} positions`}
                sx={{
                  height: 22,
                  fontWeight: 600,
                  fontSize: 11,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  color: "primary.dark",
                }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={sportLabel}
                sx={{
                  height: 22,
                  fontWeight: 600,
                  fontSize: 11,
                  borderColor: "primary.light",
                  color: "primary.main",
                }}
              />
              {selectedNames.length > 0 && (
                <Chip
                  size="small"
                  color="primary"
                  label={
                    selectedNames.length === 1
                      ? `Filtered: ${selectedNames[0]}`
                      : `Filtered: ${selectedNames.length} positions`
                  }
                  onClick={(e) => e.stopPropagation()}
                  onDelete={(e) => {
                    e.stopPropagation();
                    onClearPositions?.();
                  }}
                  sx={{
                    height: 22,
                    fontWeight: 600,
                    fontSize: 11,
                  }}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Abbreviations and values used in the Position dropdown below
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 1,
          }}
        >
          {positions.map(({ value, name }) => {
            const isSelected = selectedPositions.includes(value);
            return (
            <Box
              key={value}
              onClick={() => onTogglePosition?.(value)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 1.5,
                py: 1.25,
                borderRadius: 1.5,
                cursor: "pointer",
                bgcolor: (theme) =>
                  isSelected
                    ? alpha(theme.palette.primary.main, 0.12)
                    : "background.paper",
                border: "1px solid",
                borderColor: isSelected ? "primary.main" : "divider",
                boxShadow: (theme) =>
                  isSelected
                    ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.4)}`
                    : "none",
                transition: "border-color 0.15s, box-shadow 0.15s",
                "&:hover": {
                  borderColor: "primary.light",
                  boxShadow: (theme) =>
                    `0 0 0 1px ${alpha(theme.palette.primary.main, 0.16)}`,
                },
              }}
            >
              <Box
                sx={{
                  minWidth: 40,
                  height: 32,
                  px: 0.75,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  color: "primary.dark",
                  fontWeight: 800,
                  fontSize: 12,
                  letterSpacing: 0.3,
                }}
              >
                {getPositionLetter(value)}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  noWrap
                  title={name}
                >
                  {name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Value {value}
                </Typography>
              </Box>
            </Box>
            );
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
